import { AfterViewInit, ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, inject, input, OnInit, output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { CalculatorFormComponent } from "../../../components/calculator-form/calculator-form.component";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";
import { TExchangeTypes, TransactionCalcResponseResultData } from "../../../services/api-types/exchange.types";
import { NgxMaskDirective, NgxMaskPipe } from "ngx-mask";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { SafeHtmlPipe } from "../../../pipes/safe-html.pipe";
import { TRANSACTION_NULL_DATA } from "../../../db/calc.db";
import { CheckboxComponent } from "../../../components/checkbox/checkbox.component";
import { CommonModule, NgClass } from "@angular/common";
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { DashboardService } from '../../../services/api/dashboard.service';
import { MessageService } from '../../../services/helpers/message.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';

@Component({
  selector: 'app-home-calculator',
  imports: [
    NgxMaskDirective,
    ReactiveFormsModule,
    CommonModule,
    NgClass,
    TranslatePipe,
    SafeHtmlPipe,
  ],
  templateUrl: './home-calculator.component.html',
  styleUrls: ['./home-calculator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeCalculatorComponent implements OnInit, AfterViewInit {
  #translate = inject(TranslateService);
  data = signal<TransactionCalcResponseResultData | null>(null);
  actionType = signal<TExchangeTypes>('BUY');

  #destroyRef = inject(DestroyRef);
  #dashboardService = inject(DashboardService);
  #message = inject(MessageService);

  prev_rate = '-'
  amount = new FormControl<null | number>(null, Validators.compose([Validators.required]));
  result = new FormControl<null | string>(null, Validators.compose([Validators.required]));

  type = signal<TExchangeTypes>('BUY');

  onActionChange = output<TExchangeTypes>();
  setedData = output<TransactionCalcResponseResultData | null>();

  ngOnInit(): void {
    if (!this.data()) {
      this.#calcDefaultRate()
    }

    this.data.set(TRANSACTION_NULL_DATA);

    this.amount.valueChanges.pipe(
      takeUntilDestroyed(this.#destroyRef),
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe((val: null | number) => {
      if (val && !isNaN(val)) {
        const amount = Number(val);
        this.#calculator(amount);
      } else {
        this.data.set(null);
      }
    });
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (this.data() && this.data()!.creditAmount > 0) {
      const result = this.actionType() === 'BUY' ? (this.data()!.debitAmount / 100) : (this.data()!.creditAmount / 100);


      this.result.setValue(result.toLocaleString('ru-RU'));
    }
  }

  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;
  @ViewChild('caretEl') caretEl!: ElementRef<HTMLDivElement>;

  ngAfterViewInit() {
    const input = this.inputEl.nativeElement;
    const caret = this.caretEl.nativeElement;

    const updateCaret = () => {
      // Получаем позицию курсора в input
      const pos = input.selectionStart ?? 0;

      // Создаём span для измерения ширины текста до курсора
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.font = window.getComputedStyle(input).font;
      span.textContent = input.value.substring(0, pos) || ' ';
      document.body.appendChild(span);

      const width = span.getBoundingClientRect().width;
      caret.style.left = width + 'px';

      document.body.removeChild(span);
    };

    input.addEventListener('input', updateCaret);
    input.addEventListener('click', updateCaret);
    input.addEventListener('keyup', updateCaret);
    input.addEventListener('select', updateCaret);

    updateCaret();

    gsap.registerPlugin(ScrollTrigger);

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: '#answerSection',
        toggleActions: 'play none none none',
        start: 'top 80%',
      },
    });

    tl.from('[data-animate="title"]', {
      opacity: 0,
      x: -60,
      duration: 0.5,
      ease: 'power2.out',
    },)
      .from('[data-animate="rightCol"]', {
        opacity: 0,
        x: 60,
        duration: 0.5,
        ease: 'power2.out',
      }, '-=0.3')
      .from('[data-animate="leftCol"]', {
        opacity: 0,
        x: -60,
        duration: 0.5,
        ease: 'power2.out',
      }, '-=0.2')
      .from('[data-animate="info1"]', {
        opacity: 0,
        y: 30,
        duration: 0.4,
      }, '-=0.25')
      .from('[data-animate="info2"]', {
        opacity: 0,
        y: 30,
        duration: 0.4,
      }, '-=0.3')
      .from('[data-animate="rightForm"]', {
        opacity: 0,
        y: 50,
        duration: 0.5,
        ease: 'power2.out',
      }, '-=0.25');
  }


  setData(resultData: any): void {
    const item = { ...resultData };
    if (item.creditAmount > 0) {
      item.timeQuickDisplay = this.getHours(item.timeQuick);
      item.timeDisplay = this.getHours(item.time);
    }
    this.data.set(item);

  }

  setAction(action: TExchangeTypes): void {
    this.actionType.set(action);
    console.log(this.amount.value)
    if (this.amount.value) {
      this.#calculator(Number(this.amount.value))
    } else {
      this.#calcDefaultRate()
    }
  }

  getHours(time: number): string {
    const { hour, hours, minutes } = this.#translate.instant('public');
    if (minutes < 60) {
      return `${time} ${minutes}`
    } else {
      const h = Math.floor(time / 60);
      const hDisplay = h === 0 ? '' : h > 1 ? `${h} ${hours}` : `${h} ${hour}`
      const min = time % 60;
      const minDisplay = min > 0 ? `${min} ${minutes}` : '';
      return `${hDisplay} ${minDisplay}`;
    }
  }

  async #calcDefaultRate(): Promise<void> {
    try {
      const res = await this.#dashboardService.infoCalc({
        amount: 100 * 100,
        type: this.actionType(),
        currency: 'UZS'
      });

      if (res && res.success) {
        const data: TransactionCalcResponseResultData = {
          ...res.result.data,
          calcAmount: 100,
        }
        this.data.set(data);
      }
    } catch (err) {
      console.log(err);
    }
  }

  async #calculator(amount: number): Promise<void> {
    try {
      const res = await this.#dashboardService.infoCalc({
        amount: amount * 100,
        type: this.actionType(),
        currency: 'UZS'
      });

      if (res && !res.success) {
        this.#message.showMessage(res.result.message ?? 'Лимит!', 'warning');
        this.result.setValue(null);
        this.setedData.emit(TRANSACTION_NULL_DATA);
        this.data.set(null);
        return;
      }

      if (res && res.success) {
        const resultQuick = this.actionType() === 'BUY' ? (res.result.data.debitAmountQuick / 100) : (res.result.data.creditAmountQuick / 100);
        this.result.setValue(resultQuick.toLocaleString('ru-RU'));

        const data: TransactionCalcResponseResultData = {
          ...res.result.data,
          calcAmount: amount,
        }
        this.data.set(data);
        this.setedData.emit(data);
      }
    } catch (err) {
      console.log(err);
    }
  }

  placeholderRate = computed(() => {
    const rawRate = this.data()?.rate
    if (rawRate) {
      const rate = rawRate / 100;

      this.prev_rate = rate.toLocaleString('ru-RU');
    }

    return this.prev_rate
  });

  onTabChange(event: any): void {
    const action = event.target.id.toUpperCase() as TExchangeTypes;
    this.actionType.set(action);
    this.onActionChange.emit(action);
    this.setedData.emit(TRANSACTION_NULL_DATA);

    this.amount.setValue(null);
    this.result.setValue(null);
  }
}
